import { KeyboardEvent, MouseEvent, RefObject, useCallback, useContext, useEffect, useState, useSyncExternalStore } from 'react';
// eslint-disable-next-line arkestro-local/no-react-hot-renderer -- pending native conversion of the NSB cells
import { HotRendererProps, HotTableRef } from '@handsontable/react-wrapper';
import useTranslation from 'next-translate/useTranslation';
import { Button, combineClassNames } from '@arkestro/arkestro-design-system';
import { NsbFlavorsResponse } from '@api/SmartBaselineFlavorsApi';
import { NsbSearchItem } from '@api/SmartBaselineSearchApi';
import { ID } from 'types';
import { LineItemsContext } from '@providers/LineItemsProvider/LineItemsContext';
import { UserContext } from '@providers/UserProvider/UserProvider';
import { DEFAULT_PRECISION, displayCurrencyWithPrecision } from '@utils/currency';
import { NO_DATA_DISPLAY } from '@utils/data';
import { LineItemTableColumnSettings } from '@components/bid_request/details/types';
// eslint-disable-next-line import/no-cycle -- runtime-only (handler) usage; QuickPicker <-> BenchmarkRenderer cycle is intentional (co-located, never crosses a PR boundary)
import { showSmartBaselineQuickPicker } from '@components/bid_request/details/LineItemsTable/SmartBaselineQuickPicker';
import { parseBuiltBaselineDetails } from '@features/nsb-build-baseline/built-baseline-details';
import { appliedSmartBaselineKeys } from './appliedSmartBaselineStore';
import { getResolvedItemNameToUse, subscribeResolvedItemNameToUse } from './nsbResolvedItemBridge';
import { registerNsbEventResetter } from './nsbEventReset';
import { getCachedFlavors, subscribeFlavorsCache } from './nsbFlavorsCache';
import {
  INTELLIGENT_COUNTER_OFFER,
  MANUAL_ENTRY,
  MIRROR_STARTING_PRICE,
  getBaselineSelection,
  subscribeNsbSelection,
} from './nsbSelectionStore';
import { getEffectivePriceForFlavor } from './nsbFlavorPrice';
import { getPriceColIdx } from './columnIndexHelpers';
import {
  getLineItemId,
  getNameColIdx,
  userSelectedFlavorKeys,
} from './SmartBaselineBenchmarkRenderer';
import { parseCellNumber } from './parseCellNumber';
import { noteNsbCellPainted } from './nsbApplyStore';
import { isOverrideAllowed, subscribeLotOverride } from './nsbOverrideStore';
import { clearNsbRowConfigColumn, registerNsbRowHydrator, setNsbRowAutoSource, setNsbRowConfigColumn } from './nsbRowConfigStore';
import { invalidateRow, markSatisfiedFromStorage, needsRecompute } from './nsbRecomputeGate';
import { isUserProvided } from './userProvidedStore';
import { UserProvidedBaselineCell } from './UserProvidedBaselineCell';
import {
  BUILT_BASELINE_LABEL_KEY,
  FLAVOR_DEFS,
  ICO_LABEL_KEY,
  translateFlavorLabel,
} from './flavorDefs';
import styles from './NsbPriceCell.module.scss';

interface BaselinePriceRendererProps extends Partial<HotRendererProps> {
  // A getter (not the array): the element lives inside `columns`, so holding the array on props would
// make `columns[i].component.props.columnSettings === columns` — a self-reference that sends
// Handsontable's cycle-detection-free settings deep-compare into infinite recursion. Calling the
// getter yields the same live array without storing it on the settings graph.
  columnSettings: () => LineItemTableColumnSettings[];
  // The event's LIVE currency, threaded from generateTableConfiguration — the same value the
  // native price columns and SuggestedPriceCellRenderer format with. Deliberately NOT read from
  // LineItemsContext's `request`: that is the static server prop, while the grid's currency is
  // reducer state the header's currency selector mutates (CURRENCY_CHANGE), so the two diverge.
  currency: string;
  hotRef: RefObject<HotTableRef>;
  lotId: ID;
}

// Rows where the Baseline column has already seeded its own (non-mirror) price into lineItem.price.
const baselineAutoAppliedKeys = new Set<string>();
// sbKey → the line-item name seeded there; survives remounts so we re-seed only on a real item change.
const baselineAppliedNameBySbKey = new Map<string, string>();
// Advance a row's seeded name — used by the rename modal's "Only Change Name" to suppress the
// baseline name-changed reset so the existing baseline is kept after a rename.
export const setBaselineAppliedName = (sbKey: string, name: string): void => { baselineAppliedNameBySbKey.set(sbKey, name); };
// Rows whose baseline was MANUALLY typed over (non-mirror only) → shown as "Manual Entry".
const baselineManualKeys = new Set<string>();
// sbKey → the baseline price this column last SEEDED into the cell. The manual-entry detector compares
// the cell value against THIS (like the negotiation column's appliedSmartBaselineKeys), NOT the live
// dropdown selection — so a flavor change (selection updates a beat before the cell re-seeds) can't be
// mis-read as a manual edit and wrongly flip the row to "Manual Entry".
const baselineAppliedPriceBySbKey = new Map<string, number>();
// Per-row baseline flavor the buyer picked via the link (non-mirror override). Kept SEPARATE from the
// negotiation column's appliedSmartBaselineKeys: both columns share one sbKey, so a single map would
// make an NP pick and a BP pick clobber each other.
const baselineUserFlavorBySbKey = new Map<string, { flavorLabel: string; price: number }>();
export const setBaselineUserFlavor = (sbKey: string, flavorLabel: string, price: number): void => {
  baselineUserFlavorBySbKey.set(sbKey, { flavorLabel, price });
};
// Drop a row's per-row baseline flavor pick (e.g. when the buyer switches that row to manual entry).
export const clearBaselineUserFlavor = (sbKey: string): void => { baselineUserFlavorBySbKey.delete(sbKey); };
// Per-row refresh so the BP cell re-renders the instant a pick lands (mirrors the NP column's refresh).
const baselineRefreshCallbacks = new Map<number,() => void>();
export const triggerBaselineRefresh = (row: number): void => { baselineRefreshCallbacks.get(row)?.(); };
// TEST-ONLY: prime a completed auto-seed for a row (the applied flag + the last-seeded price) so specs can
// reproduce the flavor-change gap — the live selection has already moved to a new flavor while the cell and
// the applied price still hold the previous seed. Not used in production: the seeder owns both of these and
// sets them in lock-step with the cell, so there is no runtime path that sets one without the other.
export const primeBaselineSeedForTest = (sbKey: string, price: number): void => {
  baselineAutoAppliedKeys.add(sbKey);
  baselineAppliedPriceBySbKey.set(sbKey, price);
};

// DISPLAY only — deliberately not baselineUserFlavorBySbKey, which marks a row as the buyer's and so
// protects it from a later event-level method change.
const baselineStoredBySbKey = new Map<string, { flavorLabel: string; price: number }>();

// Clear the baseline column's per-row applied-state on every event change (see nsbEventReset).
registerNsbEventResetter(() => {
  baselineAutoAppliedKeys.clear();
  baselineAppliedNameBySbKey.clear();
  baselineManualKeys.clear();
  baselineUserFlavorBySbKey.clear();
  baselineStoredBySbKey.clear();
  baselineAppliedPriceBySbKey.clear();
});

// Rebuild the Baseline column's per-row state from a row's saved source on load. Buyer-resolved rows keep
// the protected per-row pick; default-following rows are display-only, so a method change still moves them.
registerNsbRowHydrator(entries => {
  entries.forEach(({ baselinePrice, buyerPicked, config, sbKey }) => {
    if (!config.baseline) return;
    // Mirror stores no price of its own: the cell follows the negotiation price. Marking the row satisfied
    // would also stop the negotiation seeder (the gate is shared), so a mirrored row is left to that column.
    if (config.baseline === MIRROR_STARTING_PRICE) return;
    markSatisfiedFromStorage(sbKey);
    // A Built Baseline commit persists a JSON blob here instead of a flavor id or MANUAL_ENTRY (see
    // nsb-build-baseline/built-baseline-details.ts). BPU shares the SAME appliedSmartBaselineKeys
    // entry NSP's hydrator writes (Build Baseline commits one price + label to both columns
    // unconditionally, with no independent per-column flavor pick), so this is the one no-match case
    // this file writes there directly rather than into its own baselineUserFlavorBySbKey/
    // baselineStoredBySbKey maps.
    if (parseBuiltBaselineDetails(config.baseline)) {
      appliedSmartBaselineKeys.set(sbKey, { confScore: 0, flavorLabel: BUILT_BASELINE_LABEL_KEY, price: Number(baselinePrice) });
      return;
    }
    if (config.baseline === MANUAL_ENTRY) { baselineManualKeys.add(sbKey); return; }
    // Written by the BACKEND after round 1, not by anything in this module.
    // Without a case it misses the FLAVOR_DEFS lookup below and the row silently keeps no applied
    // state at all, so the cell renders as though nothing had ever set its price.
    if (config.baseline === INTELLIGENT_COUNTER_OFFER) {
      baselineStoredBySbKey.set(sbKey, { flavorLabel: ICO_LABEL_KEY, price: Number(baselinePrice) });
      return;
    }
    // Everything that is not a flavor id is handled above.
    const label = FLAVOR_DEFS.find(f => f.id === config.baseline)?.label;
    if (!label) return;
    if (buyerPicked) setBaselineUserFlavor(sbKey, label, Number(baselinePrice));
    else baselineStoredBySbKey.set(sbKey, { flavorLabel: label, price: Number(baselinePrice) });
  });
});

// Baseline Price per Unit cell — same price + flavor-link UI as the Negotiation column.
// Mirror Starting Price → mirrors the negotiation price; otherwise uses its own baseline flavor.
export const BaselinePriceRenderer = ({ cellProperties, columnSettings: getColumnSettings, currency, row, hotRef, lotId, value }: BaselinePriceRendererProps) => {
  const columnSettings = getColumnSettings();
  const [, setRefreshKey] = useState(0);
  const { request } = useContext(LineItemsContext);
  // Read the org's award precision from config, never as a prop — a prop can be omitted or overridden
  // at a call site, which silently shows the wrong number of decimals.
  const { company } = useContext(UserContext);
  const { award_price_precision: pricePrecision = DEFAULT_PRECISION } = company || {};
  const { t } = useTranslation('common');
  // Key by the STABLE line-item id (event-scoped), never the reused row index — same as the
  // Negotiation renderer, so both columns share one identity per line item.
  const lineItemId = getLineItemId(columnSettings, hotRef.current?.hotInstance, row);
  const sbKey = lineItemId && request?.id ? `${request.id}_${lineItemId}` : null;

  // Lets the match toast tell when the NSB prices have stopped appearing. No dep array on purpose:
  // every render of every cell counts, and filling a large event takes seconds.
  useEffect(() => { noteNsbCellPainted(); });

  // Let a pick (or any external write) force this row's BP cell to re-render immediately.
  useEffect(() => {
    if (row === undefined) return undefined;
    baselineRefreshCallbacks.set(row, () => setRefreshKey(k => k + 1));
    return () => { baselineRefreshCallbacks.delete(row); };
  }, [row]);

  const nameColIdx = getNameColIdx(columnSettings);
  const lineItemName: string = (nameColIdx >= 0 && row !== undefined ? (hotRef.current?.hotInstance?.getDataAtCell(row, nameColIdx) as string) : '') || '';

  // Re-render when Part 1 resolves this row. useSyncExternalStore re-reads the snapshot AFTER
  // subscribing, so a resolution that lands between this render and the subscription can't be missed
  // (the old useEffect-subscribe could miss it and leave this column's baseline permanently blank).
  const subscribeResolved = useCallback(
    (cb: () => void): (() => void) => {
      if (!lineItemName) return () => undefined;
      return subscribeResolvedItemNameToUse(lineItemName, cb);
    },
    [lineItemName],
  );
  const resolvedItemName = useSyncExternalStore(
    subscribeResolved,
    () => (lineItemName ? getResolvedItemNameToUse(lineItemName) : undefined),
    () => undefined,
  );
  const cacheKey = resolvedItemName ? `byname__${resolvedItemName}` : null;

  // When this row's item actually CHANGES (upload / rename) — current name differs from the one we
  // seeded here — clear the stale guard so the new item re-seeds. Guarded on a real change (not every
  // mount) so a harmless remount doesn't loop. (Mirror never seeds here, so it never records/resets.)
  useEffect(() => {
    if (!sbKey || !lineItemName) return;
    const seededName = baselineAppliedNameBySbKey.get(sbKey);
    if (seededName !== undefined && seededName !== lineItemName) {
      baselineAutoAppliedKeys.delete(sbKey);
      baselineAppliedNameBySbKey.delete(sbKey);
      baselineAppliedPriceBySbKey.delete(sbKey);
      setRefreshKey(k => k + 1);
    }
  }, [lineItemName, sbKey]);

  // Re-render (and allow re-seeding) when the event-level selection changes.
  useEffect(() => subscribeNsbSelection(() => {
    if (sbKey) baselineAutoAppliedKeys.delete(sbKey);
    // A method change re-derives a stored row; the buyer's own rows are held by the seeder's checks.
    if (sbKey && !baselineUserFlavorBySbKey.has(sbKey) && !baselineManualKeys.has(sbKey)) {
      invalidateRow(sbKey);
      baselineStoredBySbKey.delete(sbKey);
    }
    setRefreshKey(k => k + 1);
  }), [sbKey]);

  // Revert this row to the event-level default when the lot's baseline-override toggle turns OFF —
  // drop both the legacy shared flag and this column's per-row pick.
  useEffect(() => subscribeLotOverride(() => {
    if (!sbKey || !lotId) return;
    if (!isOverrideAllowed(lotId, 'baseline') && (userSelectedFlavorKeys.has(sbKey) || baselineUserFlavorBySbKey.has(sbKey))) {
      userSelectedFlavorKeys.delete(sbKey);
      baselineUserFlavorBySbKey.delete(sbKey);
      baselineAutoAppliedKeys.delete(sbKey);
      baselineAppliedPriceBySbKey.delete(sbKey);
      setRefreshKey(k => k + 1);
    }
  }), [sbKey, lotId]);

  // Reactive read of the shared flavors cache — re-renders this cell the instant its flavors land
  // (cold-load fill-in without a remount), same as the Negotiation column.
  const flavors: NsbFlavorsResponse | undefined = useSyncExternalStore(
    useCallback((cb: () => void) => subscribeFlavorsCache(cacheKey, cb), [cacheKey]),
    () => getCachedFlavors(cacheKey) ?? undefined,
    () => undefined,
  );
  const precomputes = flavors?.precomputes;
  const isMirror = getBaselineSelection() === MIRROR_STARTING_PRICE;

  // Mirror → take the negotiation price the Negotiation column applied; otherwise our own flavor.
  let price: number | null = null;
  let priceTypeLabel = '';
  // The resolved flavor id for what this cell is showing, when it came from the derived price. Recorded
  // by the seeder below — taken from the resolver rather than looked up from the label, which is lossy.
  let priceTypeFlavorId: string | null = null;
  if (isMirror) {
    // Mirror Negotiations → the Negotiation renderer keeps this row's price cell synced to the
    // negotiation price (manual or smart), so just display that value (read-only, no link).
    const mirrorVal = parseCellNumber(value);
    if (value != null && value !== '' && !Number.isNaN(mirrorVal)) price = mirrorVal;
  } else {
    // Read the pick and the stored value BEFORE the derived price: on a reload nothing was fetched, so
    // `precomputes` is absent and the saved label is all there is.
    const userFlavor = sbKey ? (baselineUserFlavorBySbKey.get(sbKey) ?? baselineStoredBySbKey.get(sbKey)) : undefined;
    if (userFlavor) {
      price = userFlavor.price;
      priceTypeLabel = userFlavor.flavorLabel;
    } else if (precomputes) {
      const { id, label, price: ownPrice } = getEffectivePriceForFlavor(precomputes, getBaselineSelection());
      if (ownPrice != null) {
        price = ownPrice;
        priceTypeLabel = label;
        priceTypeFlavorId = id;
      }
    }
  }

  // When decoupled, the Baseline column owns lineItem.price — seed it once per selection.
  useEffect(() => {
    if (isMirror || price == null || !sbKey || row === undefined) return;
    // Seeding a stored row would rewrite the same price and mark the page dirty for nothing.
    if (!needsRecompute(sbKey)) return;
    if (isUserProvided(sbKey)) return; // NSB #3/#7: buyer uploaded this baseline — never seed over it
    if (baselineUserFlavorBySbKey.has(sbKey)) return; // a per-row pick owns the value
    if (baselineManualKeys.has(sbKey)) return; // a manual override owns the value (survives reload)
    if (baselineAutoAppliedKeys.has(sbKey)) return;
    const hotInstance = hotRef.current?.hotInstance;
    const priceColIdx = getPriceColIdx(columnSettings);
    if (priceColIdx < 0 || !hotInstance) return;
    baselineAutoAppliedKeys.add(sbKey);
    baselineAppliedNameBySbKey.set(sbKey, lineItemName); // remember which item we seeded here
    baselineAppliedPriceBySbKey.set(sbKey, price); // remember the price we seeded — the detector compares against this, not the live selection
    // The id comes straight from the resolver, not a label lookup — see getEffectivePriceForFlavor.
    if (priceTypeFlavorId) setNsbRowAutoSource(sbKey, 'baseline', priceTypeFlavorId, lotId);
    hotInstance.setDataAtCell(row, priceColIdx, price);
  }, [isMirror, price, sbKey, row, hotRef, columnSettings]);

  // Manual entry (non-mirror only): if the user types a value that isn't the flavor price, flip this
  // row's baseline to "Manual Entry" (keeping the matched link). Numeric compare + the flag prevent a
  // re-write loop. In Mirror mode the cell mirrors the negotiation price, so no manual flip there.
  useEffect(() => {
    if (isMirror || !sbKey || !precomputes) return;
    const num = parseCellNumber(value);
    // A zero is a transient, not a decision — same guard as the Negotiation column, same reason: the
    // cell reads 0 mid-rebuild after a save, before the seed lands.
    if (value === '' || value == null || Number.isNaN(num) || num === 0) return;
    // Compare against the price this column last APPLIED to the cell (the per-row pick, or the last
    // auto-seed) — NOT the live dropdown selection. On a flavor change the selection updates a beat before
    // the cell is re-seeded; comparing against the live selection in that gap mis-reads the still-old cell
    // value as a manual edit and wrongly flips the row to Manual Entry. The applied price moves in lock-step
    // with the cell (both set by the seeder), so there is no such gap. Fall back to the effective flavor
    // price only before anything has been seeded (first paint / reload before the auto-seed runs).
    const userFlavor = baselineUserFlavorBySbKey.get(sbKey);
    const appliedPrice = baselineAppliedPriceBySbKey.get(sbKey);
    let flavorPrice: number | null;
    if (userFlavor) {
      flavorPrice = userFlavor.price;
    } else if (appliedPrice !== undefined) {
      flavorPrice = appliedPrice;
    } else {
      flavorPrice = getEffectivePriceForFlavor(precomputes, getBaselineSelection()).price;
    }
    if (flavorPrice == null) return;
    const manual = num.toFixed(pricePrecision) !== Number(flavorPrice).toFixed(pricePrecision);
    if (manual && !baselineManualKeys.has(sbKey)) {
      baselineManualKeys.add(sbKey);
      setNsbRowConfigColumn(sbKey, 'baseline', MANUAL_ENTRY, lotId);
      invalidateRow(sbKey);
      baselineStoredBySbKey.delete(sbKey);
      setRefreshKey(k => k + 1);
    } else if (!manual && baselineManualKeys.has(sbKey)) {
      baselineManualKeys.delete(sbKey);
      clearNsbRowConfigColumn(sbKey, 'baseline', lotId);
      setRefreshKey(k => k + 1);
    }
  }, [value, sbKey, isMirror, precomputes, pricePrecision]);

  const nsbItem: NsbSearchItem | undefined = resolvedItemName ? {
    item_id: '',
    item_id_cleaned: '',
    item_name: resolvedItemName,
    item_name_cleaned: resolvedItemName,
    label: resolvedItemName,
    lookup_type: 'name_sku',
    unit_of_measure: '',
    unit_of_measure_raw: '',
  } : undefined;

  const handleIntelClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (!sbKey || !nsbItem || !cacheKey) return;
    const data = flavors ?? getCachedFlavors(cacheKey);
    if (!data) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    showSmartBaselineQuickPicker({
      anchorRect: { bottom: rect.bottom, left: rect.left, right: rect.right, top: rect.top },
      column: 'baseline',
      columnLabel: t('line_items.reporting_baseline'),
      columnSettings,
      currency,
      eventId: request?.id,
      flavors: data,
      hotRef,
      isBaseline: true,
      lotId,
      nsbItem,
      row: row ?? 0,
      sbKey,
    });
  };

  // NSB #3/#7: the buyer uploaded this row's price. Show it labeled "User Provided", no link, no NSB
  // alternates — for BOTH Mirror and decoupled (the mirror-sync effect is guarded off for these rows, so
  // BPU holds the uploaded price here). The cell stays editable so the buyer can still change the price.
  if (sbKey != null && isUserProvided(sbKey)) {
    return (
      <UserProvidedBaselineCell
        cellRowClassName={styles.cellRow}
        currency={currency}
        labelClassName={styles.label}
        priceClassName={styles.priceText}
        pricePrecision={pricePrecision}
        value={value}
      />
    );
  }

  // Manual Entry: plain editable price cell — show the typed value, no link / no mirror display.
  if (getBaselineSelection() === MANUAL_ENTRY) {
    const manualVal = parseCellNumber(value);
    const showManual = value !== '' && value != null && !Number.isNaN(manualVal);
    return (
      <div className='cell-editor'>
        <span className={`line-item-cell-renderer__text ${styles.priceText}`}>
          {showManual ? displayCurrencyWithPrecision(manualVal, currency, pricePrecision) : NO_DATA_DISPLAY}
        </span>
      </div>
    );
  }

  // No smart-baseline price for this row (no-match, or still resolving). The column is editable, so
  // this is a plain manual-entry cell — show the typed value if present, else a dash.
  if (price == null) {
    const noMatchVal = parseCellNumber(value);
    const showNoMatch = value !== '' && value != null && !Number.isNaN(noMatchVal);
    // A "Build Baseline" commit (see nsb-build-baseline) writes the SAME price + label to both the
    // Negotiation and Baseline columns unconditionally (no independent per-column flavor pick for
    // this path -- by design). It only sets the Negotiation column's shared appliedSmartBaselineKeys
    // map (this column keeps its own SEPARATE tracking for independent flavor picks -- see the
    // comment on baselineUserFlavorBySbKey above), so read that same map here for this one case.
    const noMatchAppliedMeta = sbKey ? appliedSmartBaselineKeys.get(sbKey) : undefined;
    if (noMatchAppliedMeta && noMatchAppliedMeta.flavorLabel === BUILT_BASELINE_LABEL_KEY) {
      return (
        <div className={`cell-editor ${styles.cellRow}`}>
          <span className={combineClassNames('line-item-cell-renderer__text', styles.priceText)}>
            {displayCurrencyWithPrecision(noMatchAppliedMeta.price, currency, pricePrecision)}
          </span>
          <span className={combineClassNames('line-item-cell-renderer__text', 'line-item-cell-renderer__link', styles.label)}>
            {translateFlavorLabel(t, noMatchAppliedMeta.flavorLabel)}
          </span>
        </div>
      );
    }
    // Non-mirror with no baseline → plain Manual Entry (editable, "Manual Entry" label, no link).
    // Mirror with no value → just a dash (it follows the negotiation).
    return (
      <div className={`cell-editor ${!isMirror ? styles.cellRow : ''}`}>
        <span className={combineClassNames('line-item-cell-renderer__text', showNoMatch ? styles.priceText : styles.placeholder)}>
          {showNoMatch ? displayCurrencyWithPrecision(noMatchVal, currency, pricePrecision) : NO_DATA_DISPLAY}
        </span>
        {!isMirror && (
          <span className={combineClassNames('line-item-cell-renderer__text', 'line-item-cell-renderer__link', styles.label)}>{t('line_items.manual_entry')}</span>
        )}
      </div>
    );
  }

  // Read-only ONLY in Mirror mode (the cell mirrors the negotiation price). In non-mirror mode it's
  // editable so the user can manually type — which flips this row to "Manual Entry".
  const hasBaselineValue = value !== '' && value != null && !Number.isNaN(parseCellNumber(value));
  // eslint-disable-next-line no-param-reassign -- intentional: the cell renderer sets its OWN cell's read-only meta (Handsontable pattern, keyed to this exact cell)
  if (cellProperties && hasBaselineValue && isMirror) cellProperties.readOnly = true;

  // Non-mirror manual edit → show the typed value + "Manual Entry" (keeping the matched link).
  const isBaselineManual = !isMirror && sbKey != null && baselineManualKeys.has(sbKey);
  const displayPrice = isBaselineManual ? parseCellNumber(value) : price;
  const displayLabel = isBaselineManual ? t('line_items.manual_entry') : translateFlavorLabel(t, priceTypeLabel);

  return (
    <div className={`cell-editor ${styles.cellRow}`}>
      <span className={combineClassNames('line-item-cell-renderer__text', styles.priceText)}>
        {displayCurrencyWithPrecision(displayPrice, currency, pricePrecision)}
      </span>
      {/* Mirror → read-only copy of the negotiation price (no link). Own-flavor / manual → flavor or "Manual Entry" link. */}
      {!isMirror && (
        <Button
          className={combineClassNames('line-item-cell-renderer__text', 'line-item-cell-renderer__link', styles.link)}
          onClick={handleIntelClick}
          onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter') handleIntelClick(e as any); }}
          type='link'
        >
          {displayLabel}
        </Button>
      )}
    </div>
  );
};
